/**
 * /admin/transactions (BUILD_SPEC.md §10.6, §6.3).
 *
 * Lists every transaction (paginated, searchable) with row actions for
 * archive, edit (notes only — the §6.3 PUT contract), and delete.
 * Create is wired through a modal `<dialog>` form using React Hook
 * Form + Zod with the canonical region/serviceType lists from §10.7-8.
 *
 * Mutations invalidate the relevant React Query keys so the dashboard
 * grid and this list stay in sync without a manual refresh.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import { Pencil, Archive, Trash2, Play, Pause as PauseIcon, FileDown, Download, X as XIcon } from "lucide-react";
import { playErrorTone } from "@/lib/sounds";
import {
  archiveTransaction,
  createTransaction,
  deleteTransaction,
  listTransactions,
  pauseTransaction,
  resumeTransaction,
  unarchiveTransaction,
  updateTransactionNotes,
} from "@/api/transactions";
import { useProgressStore } from "@/stores/progressStore";
import { downloadSnapshotExport, downloadTransactionExport } from "@/api/exports";
import type { Transaction } from "@/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CANONICAL_REGIONS, CANONICAL_SERVICE_TYPES } from "@/lib/regions";

const CreateSchema = z.object({
  requestNumber: z.string().trim().min(1).max(64),
  year: z.coerce.number().int().min(1900).max(2200),
  region: z.string().min(1),
  serviceType: z.string().min(1),
  followUpPhone: z.string().trim().min(7).max(32).regex(/^[+0-9 ()\-]+$/u),
  notes: z.string().trim().max(4000).optional(),
});
type CreateValues = z.infer<typeof CreateSchema>;

/** Shape of the `details.existing` payload returned by POST
 *  /api/transactions when the request collides on the
 *  (requestNumber, year, region) unique tuple — v1.1 A6 contract. */
interface DuplicateExisting {
  id: string;
  requestNumber: string;
  year: number;
  region: string;
  ownerName: string | null;
}

export function TransactionsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [deleting, setDeleting] = useState<Transaction | null>(null);
  const [archiving, setArchiving] = useState<Transaction | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateExisting | null>(null);

  // Debounce-light: the list refetches on every keystroke. Fine for
  // the office's data volume (~60 rows); add a debounce later if it
  // becomes a problem.
  const query = useQuery({
    queryKey: ["transactions", "admin", { page, search }],
    queryFn: () => listTransactions({ page, limit: 50, ...(search ? { search } : {}) }),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["transactions"] });
  };

  const progressStart = useProgressStore((s) => s.start);
  const progressEnd = useProgressStore((s) => s.end);

  const createMutation = useMutation({
    mutationFn: (values: CreateValues) => {
      // v1.1 C4 — tick up the global progress counter for the
      // duration of this single-add API call. onSettled below ticks
      // it back down so the bar fades out 500 ms after completion.
      progressStart();
      // exactOptionalPropertyTypes: only forward `notes` when the user
      // actually entered something so the API input matches its
      // non-undefined optional shape.
      const payload: Parameters<typeof createTransaction>[0] = {
        requestNumber: values.requestNumber,
        year: values.year,
        region: values.region,
        serviceType: values.serviceType,
        followUpPhone: values.followUpPhone,
      };
      if (values.notes && values.notes.length > 0) payload.notes = values.notes;
      return createTransaction(payload);
    },
    onSuccess: () => {
      setCreating(false);
      invalidate();
    },
    onError: (err) => {
      // v1.1 A6: surface the friendly "already exists" modal when the
      // API returns the DUPLICATE_TRANSACTION envelope. Everything else
      // falls through to the in-dialog error banner.
      if (err instanceof AxiosError && err.response?.status === 409) {
        const body = err.response.data as
          | { error?: { code?: string; details?: { existing?: DuplicateExisting } } }
          | undefined;
        const existing = body?.error?.details?.existing;
        if (body?.error?.code === "DUPLICATE_TRANSACTION" && existing) {
          setCreating(false);
          setDuplicate(existing);
        }
      }
    },
    onSettled: () => {
      // Tick down regardless of success / failure so the bar always
      // settles cleanly.
      progressEnd();
    },
  });
  const archiveMutation = useMutation({
    mutationFn: archiveTransaction,
    onSuccess: () => invalidate(),
  });
  const unarchiveMutation = useMutation({
    mutationFn: unarchiveTransaction,
    onSuccess: () => invalidate(),
  });
  const pauseMutation = useMutation({ mutationFn: pauseTransaction, onSuccess: () => invalidate() });
  const resumeMutation = useMutation({ mutationFn: resumeTransaction, onSuccess: () => invalidate() });
  const deleteMutation = useMutation({
    mutationFn: deleteTransaction,
    onSuccess: () => invalidate(),
  });
  const editMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string | null }) =>
      updateTransactionNotes(id, notes),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const snapshotMutation = useMutation({
    mutationFn: () => downloadSnapshotExport("ACTIVE"),
  });
  const txExportMutation = useMutation({
    mutationFn: (id: string) => downloadTransactionExport(id),
  });

  // §11.2 step 17: disable the snapshot button when no rows are
  // visible so we don't generate an empty workbook for the user.
  const hasRows = (query.data?.items.length ?? 0) > 0;

  const totalPages = useMemo(() => {
    if (!query.data) return 1;
    return Math.max(1, Math.ceil(query.data.total / query.data.limit));
  }, [query.data]);

  const columns: Column<Transaction>[] = [
    {
      key: "requestNumber",
      header: t("tx.requestNumber"),
      // Explicit slash separator + same-size both halves so the year is
      // visually distinct from the request number, not a styling suffix.
      // The previous "5 [year-small]" reading was easy to misread as the
      // year being concatenated INTO the number.
      render: (r) => (
        <span className="rtl-isolate font-medium text-fg dark:text-fg-dark">
          {r.requestNumber}
          <span className="mx-1 text-muted dark:text-muted-dark">/</span>
          {r.year}
        </span>
      ),
    },
    {
      key: "owner",
      header: t("tx.ownerName"),
      // ownerName is null until the scraper's first successful poll
      // populates it (§5.2). Show a localized "pending" affordance so
      // the empty cell doesn't read as broken data.
      render: (r) =>
        r.ownerName ? (
          <span className="rtl-isolate">{r.ownerName}</span>
        ) : (
          <span className="rtl-isolate italic text-muted dark:text-muted-dark">
            {t("tx.ownerPending")}
          </span>
        ),
    },
    {
      key: "region",
      header: t("tx.region"),
      render: (r) => <span className="rtl-isolate">{r.region}</span>,
      hideOnMobile: true,
    },
    {
      key: "serviceType",
      header: t("tx.serviceType"),
      render: (r) => <span className="rtl-isolate">{r.serviceType}</span>,
      hideOnMobile: true,
    },
    {
      key: "status",
      header: t("tx.status"),
      render: (r) => (
        <span className="inline-flex rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs">
          {t(`tx.${r.status}`)}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("common.actions"),
      render: (r) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(r)}
            aria-label={t("common.edit")}
            title={t("common.edit")}
            className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {r.status === "PAUSED" ? (
            <button
              type="button"
              onClick={() => resumeMutation.mutate(r.id)}
              aria-label={t("tx.resume")}
              title={t("tx.resume")}
              className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
            >
              <Play className="h-4 w-4" />
            </button>
          ) : r.status === "ACTIVE" ? (
            <button
              type="button"
              onClick={() => pauseMutation.mutate(r.id)}
              aria-label={t("tx.pause")}
              title={t("tx.pause")}
              className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
            >
              <PauseIcon className="h-4 w-4" />
            </button>
          ) : null}
          {r.status === "ARCHIVED" ? (
            <button
              type="button"
              onClick={() => unarchiveMutation.mutate(r.id)}
              aria-label={t("tx.unarchive")}
              title={t("tx.unarchive")}
              className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
            >
              <Archive className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setArchiving(r)}
              aria-label={t("tx.archive")}
              title={t("tx.archive")}
              className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40"
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => txExportMutation.mutate(r.id)}
            disabled={txExportMutation.isPending}
            aria-label={t("export.transaction")}
            title={t("export.transaction")}
            className="rounded-md p-1 text-muted hover:bg-hairline/40 dark:text-muted-dark dark:hover:bg-hairline-dark/40 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setDeleting(r)}
            aria-label={t("common.delete")}
            title={t("common.delete")}
            className="rounded-md p-1 text-rejection hover:bg-rejection-bg dark:text-rejection-dark dark:hover:bg-rejection-dark-bg/40"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-bold text-fg dark:text-fg-dark">{t("admin.transactions")}</h2>
        <button
          type="button"
          onClick={() => snapshotMutation.mutate()}
          disabled={!hasRows || snapshotMutation.isPending}
          aria-label={t("export.snapshot")}
          title={hasRows ? t("export.snapshot") : t("export.noTransactions")}
          className="inline-flex items-center gap-1.5 rounded-md border border-hairline dark:border-hairline-dark px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 disabled:opacity-50"
        >
          <FileDown className="h-4 w-4" aria-hidden />
          {snapshotMutation.isPending ? t("export.exporting") : t("export.snapshot")}
        </button>
      </div>
      {snapshotMutation.error && (
        <p className="rounded-md bg-rejection-bg dark:bg-rejection-dark-bg/40 p-2 text-sm text-rejection dark:text-rejection-dark">
          {(snapshotMutation.error as Error).message}
        </p>
      )}

      <DataTable
        rows={query.data?.items ?? []}
        columns={columns}
        rowKey={(r) => r.id}
        search={{ value: search, onChange: (v) => { setSearch(v); setPage(1); } }}
        addButton={{
          label: t("tx.create"),
          onClick: () => {
            // Clear any leftover error state from a prior submit so the
            // dialog re-opens clean — without this, the inline error
            // banner would still show the previous DUPLICATE message
            // even though the friendly modal already handled it.
            createMutation.reset();
            setCreating(true);
          },
        }}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        isLoading={query.isLoading}
      />

      <CreateDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={(values) => createMutation.mutate(values)}
        submitting={createMutation.isPending}
        error={createMutation.error}
      />

      <EditNotesDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSubmit={(notes) => {
          if (editing) editMutation.mutate({ id: editing.id, notes });
        }}
        submitting={editMutation.isPending}
      />

      <ConfirmDialog
        open={!!archiving}
        title={t("tx.archive")}
        body={archiving ? t("tx.archiveConfirm") : ""}
        onConfirm={() => {
          if (archiving) {
            archiveMutation.mutate(archiving.id);
            setArchiving(null);
          }
        }}
        onCancel={() => setArchiving(null)}
      />

      <DuplicateTransactionDialog
        existing={duplicate}
        onClose={() => setDuplicate(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        destructive
        title={t("common.delete")}
        body={deleting ? t("tx.deleteConfirm") : ""}
        confirmLabel={t("common.delete")}
        onConfirm={() => {
          if (deleting) {
            deleteMutation.mutate(deleting.id);
            setDeleting(null);
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

/**
 * Friendly modal for the "this transaction already exists" path — v1.1 A6.
 * Replaces the previous red "Request failed" toast. Carries a deep link
 * to the existing transaction's detail page (route added in v1.1 B4).
 */
function DuplicateTransactionDialog({
  existing,
  onClose,
}: {
  existing: DuplicateExisting | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!existing) return null;
  return (
    <dialog
      open
      className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-0 backdrop:bg-fg/40"
    >
      <div className="w-[min(28rem,90vw)] p-6 space-y-3">
        <h3 className="rtl-isolate text-lg font-bold text-fg dark:text-fg-dark">
          {t("tx.duplicate.title")}
        </h3>
        <p className="text-sm text-fg dark:text-fg-dark">{t("tx.duplicate.body")}</p>
        <p className="rtl-isolate text-sm text-muted dark:text-muted-dark">
          {existing.requestNumber} / {existing.year} — {existing.region}
        </p>
        {existing.ownerName ? (
          <p className="rtl-isolate text-sm text-muted dark:text-muted-dark">
            {t("tx.duplicate.ownerLine", { owner: existing.ownerName })}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Link
            to={`/transactions/${existing.id}`}
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("tx.duplicate.viewExisting")}
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold"
          >
            {t("tx.duplicate.ok")}
          </button>
        </div>
      </div>
    </dialog>
  );
}

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateValues) => void;
  submitting: boolean;
  error: unknown;
}

function CreateDialog({ open, onClose, onSubmit, submitting, error }: CreateDialogProps) {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, formState } = useForm<CreateValues>({
    resolver: zodResolver(CreateSchema),
    defaultValues: { year: new Date().getFullYear() },
  });
  // v1.1 C5 — shake-on-backdrop-click. A key bump remounts the modal
  // wrapper element so the CSS shake animation replays each time.
  const [shakeKey, setShakeKey] = useState(0);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // Reset form whenever the dialog reopens so a previous submission's
  // values aren't sticky between opens.
  if (!open && formState.isSubmitted) reset();

  // Escape closes the modal — the same affordance as the X button.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus the first text input when the modal opens for an immediate
  // keyboard entry path; respects accessibility preferences via the
  // browser's normal focus chain.
  useEffect(() => {
    if (open) {
      window.requestAnimationFrame(() => firstFieldRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;
  const inputCls =
    "w-full rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-2 text-sm text-fg dark:text-fg-dark";

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only react to clicks DIRECTLY on the backdrop, not on bubbling
    // events from the modal content beneath.
    if (e.target !== e.currentTarget) return;
    setShakeKey((k) => k + 1);
    // Quiet error sound — trains the user to use the X button instead
    // of dismissing by clicking out. The function bypasses the master
    // mute because this is a direct response to a user gesture, not a
    // notification.
    playErrorTone();
  };

  const { ref: firstFieldRegisterRef, ...firstFieldRegister } = register("requestNumber");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-tx-title"
      onClick={onBackdropClick}
      className="fixed inset-0 z-40 flex items-center justify-center bg-fg/40 dark:bg-canvas-dark/70 p-4"
    >
      <div
        key={shakeKey}
        // The inner panel stops click propagation so a stray click on
        // a form field doesn't bubble up to onBackdropClick.
        onClick={(e) => e.stopPropagation()}
        className={`relative w-[min(36rem,90vw)] rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark shadow-xl motion-reduce:animate-none ${
          shakeKey > 0 ? "animate-shake" : ""
        }`}
      >
        {/* X close button — top-trailing edge. `end-3` flips with dir
            via the RTL plugin so it lands top-left in Arabic and
            top-right in English. */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.cancel")}
          title={t("common.cancel")}
          className="absolute end-3 top-3 rounded-md p-1 text-muted dark:text-muted-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
        >
          <XIcon className="h-4 w-4" aria-hidden />
        </button>

        <form
          onSubmit={handleSubmit((v) => onSubmit(v))}
          className="p-6 pe-10 space-y-3"
        >
          <h3 id="create-tx-title" className="text-lg font-bold text-fg dark:text-fg-dark">
            {t("tx.create")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.requestNumber")}</span>
              <input
                className={inputCls}
                {...firstFieldRegister}
                ref={(el) => {
                  firstFieldRegisterRef(el);
                  firstFieldRef.current = el;
                }}
              />
            </label>
            <label className="block text-sm">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.year")}</span>
              <input className={inputCls} type="number" {...register("year")} />
            </label>
            <label className="block text-sm">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.region")}</span>
              <select className={inputCls} {...register("region")}>
                <option value="">—</option>
                {CANONICAL_REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.serviceType")}</span>
              <select className={inputCls} {...register("serviceType")}>
                <option value="">—</option>
                {CANONICAL_SERVICE_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.followUpPhone")}</span>
              <input className={inputCls} type="tel" {...register("followUpPhone")} />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.notes")}</span>
              <textarea className={inputCls} rows={3} {...register("notes")} />
            </label>
          </div>
          {error ? (
            <p className="text-sm text-rejection dark:text-rejection-dark" role="alert">
              {(error as Error).message ?? t("common.errorTitle")}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
            >
              {t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditNotesDialogProps {
  target: Transaction | null;
  onClose: () => void;
  onSubmit: (notes: string | null) => void;
  submitting: boolean;
}

function EditNotesDialog({ target, onClose, onSubmit, submitting }: EditNotesDialogProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState("");

  // Sync local state with the targeted row whenever it changes — a
  // controlled textarea needs the prop-derived initial value plumbed
  // through useState explicitly.
  useMemo(() => {
    setNotes(target?.notes ?? "");
  }, [target]);

  if (!target) return null;
  return (
    <dialog
      open
      className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-0 backdrop:bg-fg/40"
    >
      <div className="w-[min(32rem,90vw)] p-6 space-y-3">
        <h3 className="rtl-isolate text-lg font-bold text-fg dark:text-fg-dark">
          {t("tx.edit")} — {target.requestNumber}
        </h3>
        <label className="block text-sm">
          <span className="block mb-1 text-fg dark:text-fg-dark">{t("tx.notes")}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-3 py-2 text-sm text-fg dark:text-fg-dark"
            rows={5}
          />
        </label>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onSubmit(notes.length > 0 ? notes : null)}
            disabled={submitting}
            className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </dialog>
  );
}
